import {
  RadioButton,
  RadioButtonChangeEvent,
} from "@progress/kendo-react-inputs";
import { Hint } from "@progress/kendo-react-labels";
import { useAppDispatch } from "hooks/redux";
import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showErrorMessage } from "store/errorModalSlice";

const CreateRfiTile = () => {
  // Constants
  const inputRef = useRef<HTMLInputElement>(null);
  const  sources = {
    EXISTING: "existing",
    TEMPLATE: "template" 
    }

  // Hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // State
  const [requestTitle, setRequestTitle] = useState("");
  const [titleValidClass, setTitleValidClass] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  // Handlers
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleValidClass("");
    setRequestTitle(e.target.value.trimStart());
  };

  const handleCategoryChange = useCallback(
    (e: RadioButtonChangeEvent) => {
      setSelectedCategory(e.value);
    },
    [setSelectedCategory]
  );

  const createRfi = (source: string) => {
    if (requestTitle.trim().length === 0) {
      setTitleValidClass(" is-invalid");
      dispatch(
        showErrorMessage(`You must provide a title for this request.`)
      );
      inputRef.current?.focus();
    } else {
      if (source === sources.TEMPLATE) {
        navigate("/TemplateList", {
          state: { category: selectedCategory, title: requestTitle },
        });
      } else if (source === sources.EXISTING)
        navigate("/RfiList", {
          state: { category: selectedCategory, title: requestTitle },
        });
    }
  };

  // Render

  return (
    <>
      <h1 className="h3">Create New RFI</h1>
      <h2 className="h5 mt-3">What category of goods and/or services?</h2>
      <fieldset>
        <div className="cirIconGrid mt-4 mb-4">
          <legend className="visuallyHidden">
            What category of goods and/or services?
          </legend>

          <label htmlFor="cat1">
            <RadioButton
              id="cat1"
              name="category"
              value="Facilities"
              checked={selectedCategory === "Facilities"}
              onChange={handleCategoryChange}
            ></RadioButton>
            <span className="cirIconWrapper">
              <i className="fa-solid fa-city"></i>
            </span>
            <span>Facilities</span>
          </label>

          <label htmlFor="cat2">
            <RadioButton
              id="cat2"
              name="category"
              value="General"
              checked={selectedCategory === "General"}
              onChange={handleCategoryChange}
            ></RadioButton>
            <span className="cirIconWrapper">
              <i className="fa-solid fa-ellipsis"></i>
            </span>
            <span>General</span>
          </label>

          <label htmlFor="cat3">
            <RadioButton
              id="cat3"
              name="category"
              value="Human Capital"
              checked={selectedCategory === "Human Capital"}
              onChange={handleCategoryChange}
            ></RadioButton>
            <span className="cirIconWrapper">
              <i className="fa-solid fa-users"></i>
            </span>
            <span>Human Capital</span>
          </label>

          <label htmlFor="cat4">
            <RadioButton
              id="cat4"
              name="category"
              value="Industrial Products & Services"
              checked={selectedCategory === "Industrial Products & Services"}
              onChange={handleCategoryChange}
            ></RadioButton>
            <span className="cirIconWrapper">
              <i className="fa-solid fa-industry"></i>
            </span>
            <span>Industrial Products &amp; Services</span>
          </label>

          <label htmlFor="cat5">
            <RadioButton
              id="cat5"
              name="category"
              value="Information Technology"
              checked={selectedCategory === "Information Technology"}
              onChange={handleCategoryChange}
            ></RadioButton>
            <span className="cirIconWrapper">
              <i className="fa-solid fa-microchip"></i>
            </span>
            <span>Information Technology</span>
          </label>

          <label htmlFor="cat6">
            <RadioButton
              id="cat6"
              name="category"
              value="Medical"
              checked={selectedCategory === "Medical"}
              onChange={handleCategoryChange}
            ></RadioButton>
            <span className="cirIconWrapper">
              <i className="fa-solid fa-stethoscope"></i>
            </span>
            <span>Medical</span>
          </label>

          <label htmlFor="cat7">
            <RadioButton
              id="cat7"
              name="category"
              value="Office Management"
              checked={selectedCategory === "Office Management"}
              onChange={handleCategoryChange}
            ></RadioButton>
            <span className="cirIconWrapper">
              <i className="fa-solid fa-building"></i>
            </span>
            <span>Office Management</span>
          </label>

          <label htmlFor="cat8">
            <RadioButton
              id="cat8"
              name="category"
              value="Professional Services"
              checked={selectedCategory === "Professional Services"}
              onChange={handleCategoryChange}
            ></RadioButton>
            <span className="cirIconWrapper">
              <i className="fa-solid fa-user-tie"></i>
            </span>
            <span>Professional Services</span>
          </label>

          <label htmlFor="cat9">
            <RadioButton
              id="cat9"
              name="category"
              value="Security &amp; Protection"
              checked={selectedCategory === "Security & Protection"}
              onChange={handleCategoryChange}
            ></RadioButton>
            <span className="cirIconWrapper">
              <i className="fa-solid fa-shield-halved"></i>
            </span>
            <span>Security &amp; Protection</span>
          </label>

          <label htmlFor="cat10">
            <RadioButton
              id="cat10"
              name="category"
              value="Transportation & Logistics"
              checked={selectedCategory === "Transportation & Logistics"}
              onChange={handleCategoryChange}
            ></RadioButton>
            <span className="cirIconWrapper">
              <i className="fa-solid fa-truck-fast"></i>
            </span>
            <span>Transportation &amp; Logistics</span>
          </label>

          <label htmlFor="cat11">
            <RadioButton
              id="cat11"
              name="category"
              value="Travel"
              checked={selectedCategory === "Travel"}
              onChange={handleCategoryChange}
            ></RadioButton>
            <span className="cirIconWrapper">
              <i className="fa-solid fa-plane"></i>
            </span>
            <span>Travel</span>
          </label>
        </div>
      </fieldset>
      <div className="form-group">
        <h2 className="h5">
          <label htmlFor="requestTitle">
            What is the title of this request?
          </label>
        </h2>

        <input
          type="text"
          className={`form-control ${titleValidClass}`}
          id="requestTitle"
          name="requestTitle"
          ref={inputRef}
          onChange={handleTitleChange}
          value={requestTitle}
          maxLength={256}
        />
        <Hint direction={"end"}>
          {requestTitle?.length ?? 0} / {256}
        </Hint>
      </div>
      <div className="mt-3">
        <button
          type="button"
          className="btn btn-primary k-mr-2"
          onClick={() => {
            createRfi(sources.TEMPLATE);
          }}
        >
          Create Using a Template
        </button>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => {
            createRfi(sources.EXISTING);
          }}
        >
          Copy Existing RFI
        </button>
      </div>
    </>
  );
};

export default CreateRfiTile;
